import {
  ADD_NEW_MESSAGE,
  AIAssistantActionTypes,
  CLEAR_ACTIVE_DOCUMENT,
  GET_GENERATED_PODCAST,
  SET_ACTIVE_PODCAST_ID,
  CLEAR_ACTIVE_PODCAST,
  PATCH_GENERATED_PODCAST,
  CLEAR_DOCUMENT_STREAM,
  CLEAR_TEMPORARY_ATTACHMENTS,
  CLEAR_LAST_GENERATED_IMAGE_URL,
  CLEAR_LAST_GENERATED_ARTIFACT_ID,
  RESTORE_CONTEXT_ATTACHMENTS,
  SAVE_CONTEXT_ATTACHMENTS,
  CLEAR_CONTEXT_ATTACHMENTS,
  CLEAR_TEMPORARY_CONVERSATION,
  CREATE_NEW_CONVERSATION,
  DELETE_CONVERSATION,
  GENERATE_AI_SUGGESTIONS,
  GET_ATTACHMENT,
  GET_ATTACHMENTS_REMAINING_LIMIT,
  GET_CONVERSATION,
  GET_CONVERSATIONS,
  GET_GENERATED_IMAGES_REMAINING_LIMIT,
  GET_PROMPTS_REMAINING_LIMIT,
  GET_GENERATED_ARTIFACTS,
  PATCH_GENERATED_ARTIFACT,
  GET_GENERATED_DOCUMENT,
  REMOVE_LAST_ASSISTANT_RESPONSE,
  REMOVE_TEMPORARY_ATTACHMENT,
  SAVE_MESSAGE_SENTIMENT,
  SET_ACTIVE_DOCUMENT_ID,
  SET_CURRENT_CONVERSATION_ID,
  STREAM_CONVERSATION_RESPONSE,
  STREAM_ERROR,
  STREAM_EVENT_DOCUMENT_CHUNK,
  STREAM_EVENT_DOCUMENT_DONE,
  STREAM_EVENT_DOCUMENT_METADATA,
  STREAM_EVENT_DOCUMENT_START,
  STREAM_EVENT_DOCUMENT_TITLE,
  STREAM_EVENT_DONE,
  STREAM_EVENT_MESSAGE_CHUNK,
  STREAM_EVENT_MESSAGE_METADATA,
  STREAM_EVENT_METADATA,
  STREAM_EVENT_TITLE_UPDATE,
  STREAM_EVENT_TOOL_USE_END,
  STREAM_EVENT_TOOL_USE_START,
  SYNC_GENERATED_DOCUMENT,
  UPDATE_GENERATED_DOCUMENT,
  UPDATE_GENERATED_PODCAST,
  UPLOAD_ATTACHMENT,
  UPLOAD_IMAGE,
} from '../actions/aiAssistant.actionTypes';
import { TOOL_NAME_GENERATE_IMAGE } from '../components/AIAssistant/shared/constants';
import { StoreItem } from '.';
import immutable from 'object-path-immutable';
import { AsyncActionStatus } from '../actions/common.actionTypes';
import {
  ConversationMessage,
  ConversationMessageContext,
  Conversation as ConversationType
} from '@klab-berlin/api-sdk/lib/types/common/Conversation';
import { ConversationListItem } from '@klab-berlin/api-sdk/lib/types/responses/Conversation';
import { Pagination } from '../types/AppContent';
import { conversationListSettings } from '../constants';
import { arrayToObject } from '../utils/arrayToObject';
import { ConversationNewMessage as ConversationNewMessageType } from '@klab-berlin/api-sdk/lib/types/requests/Ai';
import {
  AiGeneratedDocument,
  ConversationAttachment,
  GeneratedArtifact,
  GeneratedArtifactType,
  GeneratedPodcast,
} from '@klab-berlin/api-sdk/lib/types/responses/Ai';

export type ConversationNewMessage = ConversationNewMessageType & {
  id: string;
  isError?: boolean;
};
export type ConversationDocumentStatus = 'started' | 'done' | 'stopped';
export type ConversationDocument = {
  id: string;
  title: string;
  content: string;
  status: ConversationDocumentStatus;
};

export type NewConversation = Omit<ConversationType, 'messages' | 'type' | 'title'> & {
  messages: ConversationNewMessage[];
};

type ConversationWithNewMessages = Omit<ConversationType, 'messages'> & {
  messages: (ConversationNewMessage | ConversationMessage)[];
};

export type Conversation = ConversationWithNewMessages | NewConversation;
export type ConversationByIdState = StoreItem<Conversation & {
  rateLimit?: {
    messages?: {
      expiresAt: Date
    }
    attachments?: {
      expiresAt: Date
    }
  }
}>;

export type ConversationsPage = StoreItem<string[]>;
export type ArtifactsPage = StoreItem<GeneratedArtifact[]>;

export type ArtifactsPagination = { total: number; hasNext: boolean };

export type SuggestionsState = StoreItem<string[]> & {
  requestId: string | null;
};

export type ConversationAttachmentState = StoreItem<ConversationAttachment>;

export interface AIAssistantState {
  conversations: {
    byId: Record<string, ConversationByIdState>;
    stream: {
      conversationId: string | null;
      status: AsyncActionStatus;
      message: ConversationNewMessage | null;
      document: ConversationDocument | null;
      activeToolName: string | null;
      generatedImageId: string | null;
      generatedPodcastId: string | null;
    };
    list: {
      pages: ConversationsPage[];
      byId: Record<string, ConversationListItem>;
      pagination: Pagination;
    };
    // temporaryConversation - used to temporarily store a new conversation until
    // the back-end starts processing it. After that, the conversation is moved to "conversations"
    temporaryConversation: ConversationByIdState | null;
    currentConversationId: string | null;
    lastGeneratedArtifactId: string | null;
    attachments: {
      remainingLimit: StoreItem<number | null>;
      temporary: string[];
      byId: Record<string, ConversationAttachmentState>;
      contextAttachments: Record<string, string[]>;
    };
    images: {
      remainingLimit: StoreItem<number | null>;
      availableAgainAt: number | null;
      lastGeneratedImageUrl: string | null;
    };
    prompts: {
      remainingLimit: StoreItem<number | null>;
      availableAgainAt: number | null;
    };
    documents: {
      byId: Record<string, StoreItem<AiGeneratedDocument>>;
      activeDocumentId: string | null;
      sync: {
        id: string | null;
        status: AsyncActionStatus;
      }
    };
    podcasts: {
      byId: Record<string, StoreItem<GeneratedPodcast>>;
      activePodcastId: string | null;
    };
  };
  artifacts: {
    pages: ArtifactsPage[];
    pagination: ArtifactsPagination;
  };
  suggestions: Record<string, SuggestionsState>;
}

export enum AiLoadingStatus {
  NONE = 'none', // nothing fetched yet
  INITIAL = 'initial', // first page is loading
  PAGINATION = 'pagination', // a subsequent page is loading
  LOADED = 'loaded', // all requested pages loaded
}

const initialPagination: Pagination = {
  limit: conversationListSettings.itemsPerPage,
  offset: 0,
  total: -1,
  sortOrder: 'desc',
};

const initialState: AIAssistantState = {
  conversations: {
    byId: {},
    stream: {
      conversationId: null,
      status: AsyncActionStatus.INITIAL,
      message: null,
      document: null,
      activeToolName: null,
      generatedImageId: null,
      generatedPodcastId: null,
    },
    list: {
      pages: [],
      byId: {},
      pagination: initialPagination
    },
    temporaryConversation: null,
    currentConversationId: null,
    lastGeneratedArtifactId: null,
    attachments: {
      remainingLimit: { data: null, status: AsyncActionStatus.INITIAL },
      temporary: [],
      byId: {},
      contextAttachments: {},
    },
    images: {
      remainingLimit: { data: null, status: AsyncActionStatus.INITIAL },
      availableAgainAt: null,
      lastGeneratedImageUrl: null,
    },
    prompts: {
      remainingLimit: { data: null, status: AsyncActionStatus.INITIAL },
      availableAgainAt: null,
    },
    documents: {
      byId: {},
      activeDocumentId: null,
      sync: {
        id: null,
        status: AsyncActionStatus.INITIAL,
      },
    },
    podcasts: {
      byId: {},
      activePodcastId: null,
    },
  },
  artifacts: { pages: [], pagination: { total: -1, hasNext: false } },
  suggestions: {},
};

const aiAssistant = (
  state = initialState,
  action: AIAssistantActionTypes
): AIAssistantState => {
  switch (action.type) {
    case CREATE_NEW_CONVERSATION: {
      const { id, message, context } = action.payload;

      const newConversation = {
        id,
        messages: [message],
        context,
        createdAt: Date.now(),
        updatedAt: Date.now(),

      };

      const newConversationByIdState = {
        data: newConversation,
        status: AsyncActionStatus.LOADING
      };

      return immutable(state)
        .set(['conversations', 'temporaryConversation'], newConversationByIdState)
        .set(['conversations', 'stream', 'document'], null)
        .value();
    }

    case STREAM_CONVERSATION_RESPONSE.START: {
      const { conversationId } = action.payload;
      return immutable(state)
        .set(['conversations', 'byId', conversationId, 'status'], AsyncActionStatus.LOADING)
        .set(['conversations', 'stream', 'document'], null)
        .set(['conversations', 'stream', 'activeToolName'], null)
        .value();
    }

    case STREAM_CONVERSATION_RESPONSE.ERROR:
    case STREAM_CONVERSATION_RESPONSE.SUCCESS: {
      const { conversationId } = action.payload;
      return immutable(state)
        .set(['conversations', 'byId', conversationId, 'status'], AsyncActionStatus.READY)
        .value();
    }

    case SET_CURRENT_CONVERSATION_ID: {
      const { conversationId } = action.payload;
      return immutable(state)
        .set(['conversations', 'currentConversationId'], conversationId)
        .value();
    }

    case STREAM_EVENT_MESSAGE_CHUNK: {
      const { conversationId, message } = action.payload;
      return immutable(state)
        .set(['conversations', 'stream', 'conversationId'], conversationId)
        .set(['conversations', 'stream', 'status'], AsyncActionStatus.LOADING)
        .set(['conversations', 'stream', 'message'], message)
        .value();
    }

    case STREAM_EVENT_DOCUMENT_CHUNK: {
      const { document } = action.payload;
      const currentDocument = state.conversations.stream.document;

      // If document doesn't exist, DOCUMENT_START wasn't received - something went wrong
      if (!currentDocument) {
        return state;
      }

      const docId = currentDocument.id;
      const existingDoc = state.conversations.documents.byId[docId];

      if (!existingDoc?.data) {
        return state;
      }

      const updatedDocument: ConversationDocument = {
        ...currentDocument,
        content: currentDocument.content + document.chunk,
      };

      const activeDocument: AiGeneratedDocument = {
        ...existingDoc.data,
        content: existingDoc.data.content + document.chunk,
      };

      const updatedById = {
        ...state.conversations.documents.byId,
        [docId]: { ...existingDoc, data: activeDocument }
      };

      return immutable(state)
        .set(['conversations', 'stream', 'conversationId'], state.conversations.stream.conversationId)
        .set(['conversations', 'stream', 'document'], updatedDocument)
        .set(['conversations', 'documents', 'byId'], updatedById)
        .value();
    }

    case STREAM_EVENT_DOCUMENT_METADATA: {
      const { metadata } = action.payload;
      const currentDocument = state.conversations.stream.document;

      // If document doesn't exist, DOCUMENT_START wasn't received - something went wrong
      if (!currentDocument) {
        return state;
      }

      const oldId = currentDocument.id;
      const newId = metadata.id;
      const existingDoc = state.conversations.documents.byId[oldId];

      if (!existingDoc) {
        return state;
      }

      // Move document from temp ID to real ID
      const activeDocument: AiGeneratedDocument = {
        ...existingDoc.data!,
        id: newId,
      };

      const newById = { ...state.conversations.documents.byId };
      delete newById[oldId];
      newById[newId] = { data: activeDocument, status: AsyncActionStatus.LOADING };

      // Only update activeDocumentId if it was pointing to the old temp ID
      const updatedState = immutable(state)
        .set(['conversations', 'stream', 'document'], { ...currentDocument, id: newId })
        .set(['conversations', 'documents', 'byId'], newById);

      if (state.conversations.documents.activeDocumentId === oldId) {
        return updatedState.set(['conversations', 'documents', 'activeDocumentId'], newId).value();
      }

      return updatedState.value();
    }

    case STREAM_EVENT_DOCUMENT_TITLE: {
      const { title } = action.payload;
      const currentDocument = state.conversations.stream.document;

      // If document doesn't exist, DOCUMENT_START wasn't received - something went wrong
      if (!currentDocument) {
        return state;
      }

      const docId = currentDocument.id;
      const existingDoc = state.conversations.documents.byId[docId];

      if (!existingDoc?.data) {
        return state;
      }

      const updatedDocument: ConversationDocument = {
        ...currentDocument,
        title: currentDocument.title + title.chunk,
      };

      const activeDocument: AiGeneratedDocument = {
        ...existingDoc.data,
        title: existingDoc.data.title + title.chunk,
      };

      const updatedById = {
        ...state.conversations.documents.byId,
        [docId]: { ...existingDoc, data: activeDocument }
      };

      return immutable(state)
        .set(['conversations', 'stream', 'document'], updatedDocument)
        .set(['conversations', 'documents', 'byId'], updatedById)
        .value();
    }

    case STREAM_EVENT_DOCUMENT_START: {
      // Create a temporary ID for streaming document until we get metadata
      const tempId = `temp-${Date.now()}`;

      const activeDocument: AiGeneratedDocument = {
        id: tempId,
        title: '',
        content: '',
      };

      // Don't set as active yet - only when user clicks the document card
      return immutable(state)
        .set(['conversations', 'stream', 'document'], { id: tempId, title: '', content: '', status: 'started' })
        .set(['conversations', 'documents', 'byId', tempId],
          { data: activeDocument, status: AsyncActionStatus.LOADING })
        .value();
    }

    case STREAM_EVENT_DOCUMENT_DONE: {
      const currentDocument = state.conversations.stream.document;

      // If document doesn't exist, this is an error state - log and skip
      if (!currentDocument) {
        return state;
      }

      const docId = currentDocument.id;
      const existingDoc = state.conversations.documents.byId[docId];

      if (!existingDoc?.data) {
        return state;
      }

      const updatedDocument: ConversationDocument = {
        ...currentDocument,
        status: 'done'
      };

      const updatedById = {
        ...state.conversations.documents.byId,
        [docId]: { ...existingDoc, status: AsyncActionStatus.READY }
      };

      return immutable(state)
        .set(['conversations', 'stream', 'document'], updatedDocument)
        .set(['conversations', 'documents', 'byId'], updatedById)
        .value();
    }

    case STREAM_EVENT_METADATA: {
      const { metadata } = action.payload;
      const metadataConversationId = metadata.id;
      const temporaryConversation = state.conversations.temporaryConversation;

      let updatedState = immutable(state);

      if (temporaryConversation) {

        const mergedConversation = {
          ...temporaryConversation.data,
          ...metadata
        };

        updatedState = updatedState
          .set(['conversations', 'temporaryConversation'], null)
          .set(
            ['conversations', 'byId', metadataConversationId],
            { data: mergedConversation, status: AsyncActionStatus.LOADING } as ConversationByIdState)
          .set(['conversations', 'currentConversationId'], metadataConversationId);
      }

      const existingPages = state.conversations.list.pages;
      if (existingPages.length > 0 && existingPages[0].data) {
        const updatedPages = existingPages.map(page => ({
          ...page,
          data: page.data?.filter(id => id !== metadataConversationId) || []
        }));

        updatedPages[0] = {
          ...updatedPages[0],
          data: [metadataConversationId, ...(updatedPages[0].data || [])]
        };

        updatedState = updatedState
          .set(['conversations', 'list', 'pages'], updatedPages)
          .set(['conversations', 'list', 'byId', metadataConversationId], metadata);
      }

      return updatedState
        .merge(['conversations', 'byId', metadataConversationId, 'data'], metadata)
        .value();
    }

    case STREAM_EVENT_TITLE_UPDATE: {
      const { conversationId, title } = action.payload;
      let updatedState = immutable(state);

      if (state.conversations.byId[conversationId]) {
        updatedState = updatedState.merge(
          ['conversations', 'byId', conversationId, 'data'],
          { title }
        );
      }
      if (state.conversations.list.byId[conversationId]) {
        updatedState = updatedState.merge(
          ['conversations', 'list', 'byId', conversationId],
          { title }
        );
      }

      return updatedState.value();
    }

    case STREAM_EVENT_MESSAGE_METADATA: {
      const { messageId, metadata, conversationId } = action.payload;
      const conversation = state.conversations.byId[conversationId]?.data;

      if (!conversation) {
        return state;
      }

      return immutable(state)
        .set(
          ['conversations', 'byId', conversationId, 'data'],
          {
            ...conversation,
            messages: conversation.messages.map(message =>
              message.id === messageId ? { ...message, ...metadata } : message
            )
          }
        )
        .value();
    }

    case STREAM_EVENT_DONE: {
      const { conversationId, message } = action.payload;
      const conversation = state.conversations.byId[conversationId]?.data;

      if (!conversation) {
        return state;
      }

      // If a document, image, or podcast was generated, attach it to the message context
      const currentDocument = state.conversations.stream.document;
      const currentImageId = state.conversations.stream.generatedImageId;
      const currentPodcastId = state.conversations.stream.generatedPodcastId;
      let updatedMessage = message;

      if (currentDocument?.id && currentDocument?.status === 'done') {
        updatedMessage = {
          ...updatedMessage,
          context: {
            ...updatedMessage.context,
            generatedDocumentId: currentDocument.id,
            generatedDocumentTitle: currentDocument.title,
          }
        };
      }

      if (currentImageId) {
        updatedMessage = {
          ...updatedMessage,
          context: {
            ...updatedMessage.context,
            generatedImageId: currentImageId,
          }
        };
      }

      if (currentPodcastId) {
        updatedMessage = {
          ...updatedMessage,
          context: {
            ...updatedMessage.context,
            generatedPodcastId: currentPodcastId,
          } as ConversationMessageContext
        };
      }

      // Podcasts deliberately do not count: the onboarding celebration covers only documents and images
      const generatedArtifactId = (currentDocument?.status === 'done' ? currentDocument.id : null)
        || currentImageId
        || null;

      let next = immutable(state)
        .set(['conversations', 'stream', 'status'], AsyncActionStatus.INITIAL)
        .set(['conversations', 'stream', 'message'], null)
        .set(['conversations', 'stream', 'activeToolName'], null)
        .set(['conversations', 'stream', 'generatedImageId'], null)
        .set(['conversations', 'stream', 'generatedPodcastId'], null)
        .set(
          ['conversations', 'byId', conversationId, 'data'],
          { ...conversation, messages: [...conversation.messages, updatedMessage] }
        )
        .set(['conversations', 'byId', conversationId, 'status'], AsyncActionStatus.READY)
        .set(
          ['conversations', 'attachments', 'remainingLimit', 'data'],
          (state.conversations.attachments.remainingLimit.data ?? 0) - (message.context?.attachments?.length ?? 0)
        );

      if (generatedArtifactId) {
        next = next.set(['conversations', 'lastGeneratedArtifactId'], generatedArtifactId);
      }

      return next.value();
    }

    case STREAM_EVENT_TOOL_USE_START: {
      const { toolName } = action.payload;
      return immutable(state)
        .set(['conversations', 'stream', 'activeToolName'], toolName)
        .value();
    }

    case STREAM_EVENT_TOOL_USE_END: {
      const { toolName, remainingLimit, presignedUrl, imageId, availableAgainAt } = action.payload;
      let next = immutable(state)
        .set(['conversations', 'stream', 'activeToolName'], null);

      if (toolName === TOOL_NAME_GENERATE_IMAGE) {
        if (typeof remainingLimit === 'number') {
          next = next.set(
            ['conversations', 'images', 'remainingLimit'],
            { data: remainingLimit, status: AsyncActionStatus.READY }
          );
        }
        next = next.set(['conversations', 'images', 'availableAgainAt'], availableAgainAt ?? null);
        next = next.set(['conversations', 'images', 'lastGeneratedImageUrl'], presignedUrl ?? null);
        if (imageId) {
          next = next.set(['conversations', 'stream', 'generatedImageId'], imageId);
        }
      }

      return next.value();
    }

    case STREAM_ERROR: {
      const { conversationId, errorMessage, rateLimit, streamMessage, streamDocument } = action.payload;
      const conversation = state.conversations.byId[conversationId]?.data;
      const streamConversationId = state.conversations.stream.conversationId;
      const resolvedStreamMessage = streamMessage
        ?? (streamConversationId === conversationId ? state.conversations.stream.message : null);
      const resolvedStreamDocument = streamDocument
        ?? (streamConversationId === conversationId ? state.conversations.stream.document : null);

      const updatedDocument = resolvedStreamDocument ? {
        ...resolvedStreamDocument,
        status: 'stopped' as ConversationDocumentStatus
      } : null;

      // errorMessage can be null for silent aborts (USER_SWITCHED_ACTIVE_VIEW)
      // In that case, we only persist partial content without showing an error
      const streamImageId = state.conversations.stream.generatedImageId;
      const streamPodcastId = state.conversations.stream.generatedPodcastId;
      let updatedErrorMessage = errorMessage;

      if (updatedErrorMessage && resolvedStreamDocument?.id) {
        updatedErrorMessage = {
          ...updatedErrorMessage,
          context: {
            ...updatedErrorMessage.context,
            generatedDocumentId: resolvedStreamDocument.id,
            generatedDocumentTitle: resolvedStreamDocument.title,
          }
        };
      }

      if (updatedErrorMessage && streamImageId) {
        updatedErrorMessage = {
          ...updatedErrorMessage,
          context: {
            ...updatedErrorMessage.context,
            generatedImageId: streamImageId,
          }
        };
      }

      if (updatedErrorMessage && streamPodcastId) {
        updatedErrorMessage = {
          ...updatedErrorMessage,
          context: {
            ...updatedErrorMessage.context,
            generatedPodcastId: streamPodcastId,
          } as ConversationMessageContext
        };
      }

      let updatedState = immutable(state)
        .set(['conversations', 'stream', 'status'], AsyncActionStatus.INITIAL)
        .set(['conversations', 'stream', 'message'], null)
        .set(['conversations', 'stream', 'document'], updatedDocument)
        .set(['conversations', 'stream', 'activeToolName'], null)
        .set(['conversations', 'stream', 'generatedImageId'], null)
        .set(['conversations', 'stream', 'generatedPodcastId'], null);

      // If document was being streamed, mark it as stopped in byId
      if (resolvedStreamDocument?.id) {
        const existingDoc = state.conversations.documents.byId[resolvedStreamDocument.id];
        const updatedDoc: StoreItem<AiGeneratedDocument> = {
          status: AsyncActionStatus.READY,
          data: existingDoc?.data
            ? {
              ...existingDoc.data,
              content: resolvedStreamDocument.content || existingDoc.data.content,
              title: resolvedStreamDocument.title || existingDoc.data.title,
            }
            : {
              id: resolvedStreamDocument.id,
              content: resolvedStreamDocument.content,
              title: resolvedStreamDocument.title,
            }
        };
        const updatedById: Record<string, StoreItem<AiGeneratedDocument>> = {
          ...state.conversations.documents.byId,
          [resolvedStreamDocument.id]: updatedDoc
        };
        updatedState = updatedState.set(['conversations', 'documents', 'byId'], updatedById);
      }

      // Only append streamMessage if it has different content than errorMessage
      // (avoids showing duplicate messages when abort happens after accumulating full content)
      const streamMessageContent = resolvedStreamMessage?.content?.[0]?.text;
      const errorMessageContent = errorMessage?.content?.[0]?.text;
      const shouldAppendStreamMessage = !!resolvedStreamMessage && 
        streamMessageContent !== errorMessageContent;

      if (!conversation) {
        const temporaryConversation = state.conversations.temporaryConversation;
        const temporaryMessages = temporaryConversation?.data?.messages ?? [];
        const mergedTemporaryMessages = shouldAppendStreamMessage
          ? (temporaryMessages.some(message => message.id === resolvedStreamMessage?.id)
            ? temporaryMessages
            : [...temporaryMessages, resolvedStreamMessage as ConversationNewMessage])
          : temporaryMessages;

        // Only append errorMessage if it exists (null for silent aborts)
        const finalMessages = updatedErrorMessage
          ? [...mergedTemporaryMessages, updatedErrorMessage]
          : mergedTemporaryMessages;

        const updatedConversation = {
          status: AsyncActionStatus.READY,
          data: {
            ...temporaryConversation?.data,
            messages: finalMessages,
            rateLimit
          }
        } as ConversationByIdState;

        return updatedState
          .set(['conversations', 'temporaryConversation'], updatedConversation)
          .value();
      } else {
        const messages = conversation.messages || [];
        const mergedMessages = shouldAppendStreamMessage
          ? (messages.some(message => message.id === resolvedStreamMessage?.id)
            ? messages
            : [...messages, resolvedStreamMessage as ConversationNewMessage])
          : messages;

        // Only append errorMessage if it exists (null for silent aborts)
        const finalMessages = updatedErrorMessage
          ? [...mergedMessages, updatedErrorMessage]
          : mergedMessages;

        return updatedState
          .set(['conversations', 'byId', conversationId, 'status'], AsyncActionStatus.READY)
          .set(
            ['conversations', 'byId', conversationId, 'data'],
            { ...conversation, messages: finalMessages, rateLimit }
          )
          .value();
      }
    }

    case ADD_NEW_MESSAGE: {
      const { conversationId, message } = action.payload;
      const conversation = state.conversations.byId[conversationId]?.data;
      if (!conversation) {
        return state;
      }
      const messages = conversation.messages as ConversationMessage[];

      return immutable(state)
        .set(['conversations', 'byId', conversationId, 'data'], { ...conversation, messages: [...messages, message] })
        .value();
    }

    case REMOVE_LAST_ASSISTANT_RESPONSE: {
      const { conversationId } = action.payload;
      const conversation = state.conversations.byId[conversationId]?.data;
      if (!conversation) {
        return state;
      }

      const newMessages = conversation.messages.slice(0, -1);

      return immutable(state)
        .set(['conversations', 'byId', conversationId, 'data'], { ...conversation, messages: newMessages })
        .value();
    }

    case SAVE_MESSAGE_SENTIMENT.START: {
      const { conversationId, messageId, sentimentFeedback } = action.payload;
      const conversation = state.conversations.byId[conversationId]?.data;
      if (!conversation) {
        return state;
      }

      const messages = conversation.messages as ConversationMessage[];
      const updatedMessages = messages.map(message =>
        message.id === messageId ? { ...message, sentimentFeedback } : message
      );

      return immutable(state)
        .set(
          ['conversations', 'byId', conversationId, 'data'],
          { ...conversation, messages: updatedMessages }
        )
        .value();
    }

    case SAVE_MESSAGE_SENTIMENT.ERROR: {
      const { conversationId, messageId } = action.payload;
      const conversation = state.conversations.byId[conversationId]?.data;
      if (!conversation) {
        return state;
      }

      const messages = conversation.messages as ConversationMessage[];
      const updatedMessages = messages.map(message =>
        message.id === messageId ? { ...message, sentimentFeedback: undefined } : message
      );

      return immutable(state)
        .set(
          ['conversations', 'byId', conversationId, 'data'],
          { ...conversation, messages: updatedMessages }
        )
        .value();
    }

    case GET_CONVERSATIONS.START: {
      const { query } = action.payload;
      const requestPage = Math.floor(query.offset / conversationListSettings.itemsPerPage) + 1;

      return immutable(state)
        .set(['conversations', 'list', 'pages', requestPage - 1], { status: AsyncActionStatus.LOADING })
        .value();
    }

    case GET_CONVERSATIONS.SUCCESS: {
      const { query } = action.payload;
      const { data, meta: { pagination } } = action.result;
      const requestPage = Math.floor(query.offset / conversationListSettings.itemsPerPage) + 1;

      const pageItems = data.map(conversation => conversation.id);
      const newPages = state.conversations.list.pages.map(
        (page, index) => index === requestPage - 1 ? { status: AsyncActionStatus.READY, data: pageItems } : page
      );
      const newById = arrayToObject(data, conversation => conversation.id);

      return immutable(state)
        .set(['conversations', 'list', 'pages'], newPages)
        .merge(['conversations', 'list', 'byId'], newById)
        .set(['conversations', 'list', 'pagination', 'total'], pagination.total)
        .set(['conversations', 'list', 'pagination', 'offset'], query.offset + query.limit)
        .value();
    }

    case DELETE_CONVERSATION.SUCCESS: {
      const { conversationId } = action.payload;

      let updatedState = immutable(state);

      if (state.conversations.currentConversationId === conversationId) {
        updatedState = updatedState
          .set(['conversations', 'currentConversationId'], null);
      }

      const pages = state.conversations.list.pages;
      const conversationExistsInPages = pages.some(page => page.data?.includes(conversationId));

      if (conversationExistsInPages) {
        const updatedPages = pages.map(page => ({
          ...page,
          data: page.data?.filter(id => id !== conversationId) || []
        }));

        updatedState = updatedState
          .set(['conversations', 'list', 'pages'], updatedPages);
      }

      return updatedState
        .del(['conversations', 'byId', conversationId])
        .del(['conversations', 'list', 'byId', conversationId])
        .value();
    }

    case GET_CONVERSATION.START: {
      const { conversationId } = action.payload;
      return immutable(state)
        .set(['conversations', 'byId', conversationId], { status: AsyncActionStatus.LOADING })
        .value();
    }

    case GET_CONVERSATION.SUCCESS: {
      const { conversationId } = action.payload;
      const conversation = action.result;

      return immutable(state)
        .set(['conversations', 'byId', conversationId], { status: AsyncActionStatus.READY, data: conversation })
        .value();
    }

    case GET_CONVERSATION.ERROR: {
      const { conversationId } = action.payload;
      return immutable(state)
        .set(['conversations', 'byId', conversationId], { status: AsyncActionStatus.ERROR })
        .value();
    }

    case GENERATE_AI_SUGGESTIONS.START: {
      const { requestId } = action.payload;
      return immutable(state)
        .set(['suggestions', requestId], {
          status: AsyncActionStatus.LOADING,
          requestId,
          data: []
        })
        .value();
    }

    case GENERATE_AI_SUGGESTIONS.SUCCESS: {
      const { requestId } = action.payload;
      const suggestions = action.result;
      return immutable(state)
        .set(['suggestions', requestId], {
          status: AsyncActionStatus.READY,
          requestId,
          data: suggestions
        })
        .value();
    }

    case GENERATE_AI_SUGGESTIONS.ERROR: {
      const { requestId } = action.payload;
      return immutable(state)
        .set(['suggestions', requestId], {
          status: AsyncActionStatus.ERROR,
          requestId,
          data: []
        })
        .value();
    }

    case UPLOAD_ATTACHMENT.START: {
      const { temporaryAttachmentHashName } = action.payload;
      return immutable(state)
        .set(
          ['conversations', 'attachments', 'temporary'],
          [...state.conversations.attachments.temporary, temporaryAttachmentHashName]
        )
        .set(
          ['conversations', 'attachments', 'byId', temporaryAttachmentHashName],
          { status: AsyncActionStatus.LOADING }
        )
        .value();
    }

    case UPLOAD_ATTACHMENT.SUCCESS: {
      const { temporaryAttachmentHashName } = action.payload;
      const attachment = action.result;
      const attachmentHashName = attachment.hashName;
      const temporaryAttachments = state.conversations.attachments.temporary;
      const hasDuplicates = temporaryAttachments.includes(attachmentHashName);

      const newTemporaryAttachments = hasDuplicates ?
        temporaryAttachments.filter(hashName => hashName !== temporaryAttachmentHashName) :
        temporaryAttachments.map(hashName => hashName === temporaryAttachmentHashName ? attachmentHashName : hashName);

      return immutable(state)
        .set(['conversations', 'attachments', 'temporary'], newTemporaryAttachments)
        .set(
          ['conversations', 'attachments', 'byId', attachmentHashName],
          { status: AsyncActionStatus.READY, data: attachment }
        )
        .value();
    }
    case UPLOAD_ATTACHMENT.ERROR: {
      const { temporaryAttachmentHashName } = action.payload;
      return immutable(state)
        .set(
          ['conversations', 'attachments', 'temporary'],
          state.conversations.attachments.temporary.filter(hashName => hashName !== temporaryAttachmentHashName))
        .value();
    }

    case UPLOAD_IMAGE.START:
    case UPLOAD_IMAGE.SUCCESS:
    case UPLOAD_IMAGE.ERROR: {
      // Image uploads are handled immediately in the component, no state storage needed
      return state;
    }

    case GET_ATTACHMENTS_REMAINING_LIMIT.START: {
      return immutable(state)
        .set(['conversations', 'attachments', 'remainingLimit'], { status: AsyncActionStatus.LOADING })
        .value();
    }

    case GET_ATTACHMENTS_REMAINING_LIMIT.SUCCESS: {
      return immutable(state)
        .set(
          ['conversations', 'attachments', 'remainingLimit'],
          { data: action.result.remainingLimit, status: AsyncActionStatus.READY }
        )
        .value();
    }

    case CLEAR_LAST_GENERATED_IMAGE_URL: {
      return immutable(state)
        .set(['conversations', 'images', 'lastGeneratedImageUrl'], null)
        .value();
    }

    case CLEAR_LAST_GENERATED_ARTIFACT_ID: {
      return immutable(state)
        .set(['conversations', 'lastGeneratedArtifactId'], null)
        .value();
    }

    case GET_GENERATED_IMAGES_REMAINING_LIMIT.START: {
      return immutable(state)
        .set(['conversations', 'images', 'remainingLimit', 'status'], AsyncActionStatus.LOADING)
        .value();
    }

    case GET_GENERATED_IMAGES_REMAINING_LIMIT.SUCCESS: {
      return immutable(state)
        .set(
          ['conversations', 'images', 'remainingLimit'],
          { data: action.result.remainingLimit, status: AsyncActionStatus.READY }
        )
        .set(
          ['conversations', 'images', 'availableAgainAt'],
          action.result.availableAgainAt ?? null,
        )
        .value();
    }

    case GET_GENERATED_IMAGES_REMAINING_LIMIT.ERROR: {
      return immutable(state)
        .set(['conversations', 'images', 'remainingLimit', 'status'], AsyncActionStatus.ERROR)
        .value();
    }

    case GET_PROMPTS_REMAINING_LIMIT.START: {
      return immutable(state)
        .set(['conversations', 'prompts', 'remainingLimit', 'status'], AsyncActionStatus.LOADING)
        .value();
    }

    case GET_PROMPTS_REMAINING_LIMIT.SUCCESS: {
      return immutable(state)
        .set(
          ['conversations', 'prompts', 'remainingLimit'],
          { data: action.result.remainingLimit, status: AsyncActionStatus.READY }
        )
        .set(
          ['conversations', 'prompts', 'availableAgainAt'],
          action.result.availableAgainAt,
        )
        .value();
    }

    case GET_PROMPTS_REMAINING_LIMIT.ERROR: {
      return immutable(state)
        .set(['conversations', 'prompts', 'remainingLimit', 'status'], AsyncActionStatus.ERROR)
        .value();
    }

    case REMOVE_TEMPORARY_ATTACHMENT: {
      const { attachmentHashName } = action.payload;
      return immutable(state)
        .set(
          ['conversations', 'attachments', 'temporary'],
          state.conversations.attachments.temporary.filter(hashName => hashName !== attachmentHashName)
        )
        .value();
    }

    case CLEAR_TEMPORARY_ATTACHMENTS: {
      return immutable(state)
        .set(['conversations', 'attachments', 'temporary'], [])
        .value();
    }

    case SAVE_CONTEXT_ATTACHMENTS: {
      const { contextKey, attachments } = action.payload;
      return immutable(state)
        .set(['conversations', 'attachments', 'contextAttachments', contextKey], attachments)
        .value();
    }

    case RESTORE_CONTEXT_ATTACHMENTS: {
      const { contextKey } = action.payload;
      const savedAttachments = state.conversations.attachments.contextAttachments?.[contextKey] || [];
      return immutable(state)
        .set(['conversations', 'attachments', 'temporary'], savedAttachments)
        .value();
    }

    case CLEAR_CONTEXT_ATTACHMENTS: {
      const { contextKey } = action.payload;
      return immutable(state)
        .del(['conversations', 'attachments', 'contextAttachments', contextKey])
        .value();
    }

    case GET_ATTACHMENT.START: {
      const { attachmentHashName } = action.payload;
      return immutable(state)
        .set(['conversations', 'attachments', 'byId', attachmentHashName], { status: AsyncActionStatus.LOADING })
        .value();
    }

    case GET_ATTACHMENT.SUCCESS: {
      const { attachmentHashName } = action.payload;
      return immutable(state)
        .set(
          ['conversations', 'attachments', 'byId', attachmentHashName],
          { status: AsyncActionStatus.READY, data: action.result }
        )
        .value();
    }

    case GET_ATTACHMENT.ERROR: {
      const { attachmentHashName } = action.payload;
      return immutable(state)
        .del(['conversations', 'attachments', 'byId', attachmentHashName])
        .value();
    }

    case CLEAR_TEMPORARY_CONVERSATION: {
      return immutable(state)
        .set(['conversations', 'temporaryConversation'], null)
        .value();
    }

    case CLEAR_DOCUMENT_STREAM: {
      return immutable(state)
        .set(['conversations', 'stream', 'document'], null)
        .value();
    }

    case GET_GENERATED_ARTIFACTS.START: {
      const { batch } = action.payload;
      if (batch === 0) {
        return immutable(state)
          .set(['artifacts', 'pages'], [{ status: AsyncActionStatus.LOADING }])
          .set(['artifacts', 'pagination'], { total: -1, hasNext: false })
          .value();
      }
      return immutable(state)
        .set(['artifacts', 'pages', batch], { status: AsyncActionStatus.LOADING })
        .value();
    }

    case GET_GENERATED_ARTIFACTS.SUCCESS: {
      const { batch } = action.payload;
      const { artifacts: newArtifacts, hasNext, totalCount } = action.result;

      let next = immutable(state)
        .set(['artifacts', 'pages', batch], { status: AsyncActionStatus.READY, data: newArtifacts })
        .set(['artifacts', 'pagination', 'total'], totalCount)
        .set(['artifacts', 'pagination', 'hasNext'], hasNext);

      for (const artifact of newArtifacts) {
        // only document artifacts are added to documents.byId
        if (artifact.type === GeneratedArtifactType.DOCUMENT) {
          next = next.set(
            ['conversations', 'documents', 'byId', artifact.id],
            { data: artifact, status: AsyncActionStatus.READY }
          );
        }
      }

      return next.value();
    }

    case GET_GENERATED_ARTIFACTS.ERROR: {
      const { batch } = action.payload;
      return immutable(state)
        .set(['artifacts', 'pages', batch], { status: AsyncActionStatus.ERROR })
        .value();
    }

    case PATCH_GENERATED_ARTIFACT: {
      const { artifactId, changes } = action.payload;
      const pages = state.artifacts.pages.map((page) => {
        if (!page.data?.some((artifact) => artifact.id === artifactId)) {
          return page;
        }
        return {
          ...page,
          data: page.data.map((artifact) =>
            artifact.id === artifactId ? { ...artifact, ...changes } : artifact
          ),
        };
      });

      return immutable(state)
        .set(['artifacts', 'pages'], pages)
        .value();
    }

    case GET_GENERATED_DOCUMENT.START: {
      const { documentId } = action.payload;
      return immutable(state)
        .set(['conversations', 'documents', 'byId', documentId], { status: AsyncActionStatus.LOADING })
        .set(['conversations', 'documents', 'activeDocumentId'], documentId)
        .value();
    }

    case GET_GENERATED_DOCUMENT.SUCCESS: {
      const document = action.result;
      const activeDocument: AiGeneratedDocument = {
        id: document.id,
        title: document.title,
        content: document.content,
        conversationId: document?.conversationId,
        createdAt: document?.createdAt,
        updatedAt: document?.updatedAt,
      };

      const updatedState = immutable(state)
        .set(['conversations', 'documents', 'byId', document.id],
          { data: activeDocument, status: AsyncActionStatus.READY });

      return updatedState.value();
    }

    case GET_GENERATED_DOCUMENT.ERROR: {
      const { documentId } = action.payload;
      return immutable(state)
        .set(['conversations', 'documents', 'byId', documentId], { status: AsyncActionStatus.ERROR })
        .value();
    }

    case SET_ACTIVE_DOCUMENT_ID: {
      const { documentId } = action.payload;
      return immutable(state)
        .set(['conversations', 'documents', 'activeDocumentId'], documentId)
        .set(['conversations', 'documents', 'sync', 'id'], documentId)
        .set(['conversations', 'documents', 'sync', 'status'], AsyncActionStatus.INITIAL)
        .value();
    }

    case CLEAR_ACTIVE_DOCUMENT: {
      return immutable(state)
        .set(['conversations', 'documents', 'activeDocumentId'], null)
        .set(['conversations', 'documents', 'sync', 'id'], null)
        .set(['conversations', 'documents', 'sync', 'status'], AsyncActionStatus.INITIAL)
        .value();
    }

    case GET_GENERATED_PODCAST.START: {
      const { podcastId } = action.payload;
      return immutable(state)
        .set(['conversations', 'podcasts', 'byId', podcastId], { status: AsyncActionStatus.LOADING })
        .value();
    }

    case GET_GENERATED_PODCAST.SUCCESS: {
      const podcast = action.result;
      return immutable(state)
        .set(['conversations', 'podcasts', 'byId', podcast.id],
          { data: podcast, status: AsyncActionStatus.READY })
        .value();
    }

    case GET_GENERATED_PODCAST.ERROR: {
      const { podcastId } = action.payload;
      return immutable(state)
        .set(['conversations', 'podcasts', 'byId', podcastId], { status: AsyncActionStatus.ERROR })
        .value();
    }

    case SET_ACTIVE_PODCAST_ID: {
      const { podcastId } = action.payload;
      return immutable(state)
        .set(['conversations', 'podcasts', 'activePodcastId'], podcastId)
        .value();
    }

    case CLEAR_ACTIVE_PODCAST: {
      return immutable(state)
        .set(['conversations', 'podcasts', 'activePodcastId'], null)
        .value();
    }

    case PATCH_GENERATED_PODCAST: {
      const { podcastId, changes } = action.payload;
      const existing = state.conversations.podcasts.byId[podcastId];
      if (!existing?.data) return state;
      return immutable(state)
        .set(['conversations', 'podcasts', 'byId', podcastId],
          { data: { ...existing.data, ...changes }, status: existing.status })
        .value();
    }

    case UPDATE_GENERATED_DOCUMENT.SUCCESS: {
      const { documentId, payload } = action.payload;
      const existingDoc = state.conversations.documents.byId[documentId];

      if (!existingDoc?.data) {
        return state;
      }

      const updatedDocument: AiGeneratedDocument = {
        ...existingDoc.data,
        ...(payload.title !== undefined && { title: payload.title }),
        ...(payload.content !== undefined && { content: payload.content }),
      };

      const updatedById = {
        ...state.conversations.documents.byId,
        [documentId]: { ...existingDoc, data: updatedDocument }
      };

      return immutable(state)
        .set(['conversations', 'documents', 'byId'], updatedById)
        .value();
    }

    case SYNC_GENERATED_DOCUMENT.START: {
      const { documentId } = action.payload;
      return immutable(state)
        .set(['conversations', 'documents', 'sync', 'id'], documentId)
        .set(['conversations', 'documents', 'sync', 'status'], AsyncActionStatus.LOADING)
        .value();
    }

    case SYNC_GENERATED_DOCUMENT.SUCCESS: {
      const { documentId } = action.payload;
      return immutable(state)
        .set(['conversations', 'documents', 'sync', 'id'], documentId)
        .set(['conversations', 'documents', 'sync', 'status'], AsyncActionStatus.READY)
        .value();
    }

    case SYNC_GENERATED_DOCUMENT.ERROR: {
      const { documentId } = action.payload;
      return immutable(state)
        .set(['conversations', 'documents', 'sync', 'id'], documentId)
        .set(['conversations', 'documents', 'sync', 'status'], AsyncActionStatus.ERROR)
        .value();
    }

    case UPDATE_GENERATED_PODCAST.START: {
      const { podcastId } = action.payload;
      return immutable(state)
        .set(['conversations', 'documents', 'sync', 'id'], podcastId)
        .set(['conversations', 'documents', 'sync', 'status'], AsyncActionStatus.LOADING)
        .value();
    }

    case UPDATE_GENERATED_PODCAST.SUCCESS: {
      const { podcastId } = action.payload;
      return immutable(state)
        .set(['conversations', 'documents', 'sync', 'id'], podcastId)
        .set(['conversations', 'documents', 'sync', 'status'], AsyncActionStatus.READY)
        .value();
    }

    case UPDATE_GENERATED_PODCAST.ERROR: {
      const { podcastId } = action.payload;
      return immutable(state)
        .set(['conversations', 'documents', 'sync', 'id'], podcastId)
        .set(['conversations', 'documents', 'sync', 'status'], AsyncActionStatus.ERROR)
        .value();
    }

    default:
      return state;
  }
};

export default aiAssistant;
